import { useEffect, useMemo, useRef, useState } from 'react'
import type { UsagePolicy } from '@/common/types'
import { reportError } from '@/common/errorHandling/sentryUtils'
import { resolveTermsSections } from './termsSections'

interface UseTermsAndConditionsSectionsResult {
    sections: ReturnType<typeof resolveTermsSections>['sections']
    legacyPolicies: ReturnType<typeof resolveTermsSections>['legacyPolicies']
    isCheckboxChecked: (checkboxId: string) => boolean
    toggleCheckbox: (checkboxId: string) => void
    allRequiredChecked: boolean
}

/** UI-local checkbox state for the sectioned terms-and-conditions view.
 *  Kept separate from useTermsAndConditionsFlow, which owns fetch/submit only.
 *  State is keyed per checkbox (not per section) since a section can hold more than
 *  one independently-required checkbox (e.g. FinClear Terms and Conditions). */
export function useTermsAndConditionsSections(
    policies: UsagePolicy[] | undefined,
): UseTermsAndConditionsSectionsResult {
    const { sections, legacyPolicies } = useMemo(() => resolveTermsSections(policies ?? []), [policies])

    // The sectioned view never renders legacyPolicies (unmapped slugs) — surface it so a policy
    // added on the backend without a matching section here doesn't go silently unacknowledged.
    useEffect(() => {
        if (legacyPolicies.length === 0) return

        reportError(new Error('Unmapped terms and conditions policies were not shown to the user'), {
            tags: { source: 'useTermsAndConditionsSections', function: 'resolveTermsSections' },
            extra: { slugs: legacyPolicies.map((policy) => policy.slug) },
        })
    }, [legacyPolicies])

    // Identifies the exact set of policy versions currently on screen — a new account or a
    // refetched policy version produces a different key. Compared during render (not in an
    // effect) so checkedBoxes never briefly holds stale acknowledgements from a prior identity.
    const policiesIdentity = (policies ?? [])
        .map((policy) => `${policy.slug}:${policy.versionId}`)
        .sort()
        .join('|')

    const [checkedBoxes, setCheckedBoxes] = useState<Record<string, boolean>>({})
    const previousPoliciesIdentityRef = useRef(policiesIdentity)

    if (previousPoliciesIdentityRef.current !== policiesIdentity) {
        previousPoliciesIdentityRef.current = policiesIdentity
        setCheckedBoxes({})
    }

    const isCheckboxChecked = (checkboxId: string) => Boolean(checkedBoxes[checkboxId])

    const toggleCheckbox = (checkboxId: string) => {
        setCheckedBoxes((prev) => ({ ...prev, [checkboxId]: !prev[checkboxId] }))
    }

    const allRequiredChecked = sections
        .flatMap((section) => section.checkboxes)
        .every((checkbox) => isCheckboxChecked(checkbox.id))

    return {
        sections,
        legacyPolicies,
        isCheckboxChecked,
        toggleCheckbox,
        allRequiredChecked,
    }
}
